// components/ProductpageFooter.jsx
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { addToCart } from '../slices/cartSlice';
import { RENTAL_RATE_NON_ORG, RENTAL_RATE_ORG, SHIPPING_FEE } from '../constants/constants';
import SubscriptionPlan from './SubscriptionPlan';

const ProductpageFooter = ({ product }) => {
  const [isPopUpVisible, setPopUpVisible] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // 状態
  const [serviceOption, setServiceOption] = useState('rent');
  const [inStore, setInStore] = useState(true);
  const [useSubscription, setUseSubscription] = useState(false);
  const [rentalPeriod, setRentalPeriod] = useState('default');

  // 認証情報
  const { userInfo } = useSelector((state) => state.auth);

  // --- サブスク関連の派生フラグ（ここが土台） ---
  const hasSubscription = !!userInfo?.subscription;
  const usedItems = userInfo?.subscription?.usedItems ?? 0;
  const maxItems = userInfo?.subscription?.maxItems ?? 0;
  const itemPriceLimit = userInfo?.subscription?.itemPriceLimit ?? 0;

  const priceOk = (Number(product?.price) || 0) <= itemPriceLimit;
  const slotsOk = usedItems < maxItems;        // 例）上限1着なら usedItems===0 がOK
  const canUseSubscription = hasSubscription && priceOk;

  // ラベル文言（自由に調整してください）
  const footerLabel = canUseSubscription
    ? 'ご利用中のサブスクでレンタルできます'
    : 'サブスクでお得に楽しもう!';

  // 料金計算（必要なら残す／今は購入ではなくレンタル用途想定）
  const calcBasePrice = ({
    price = 0,
    serviceOption,
    useSubscription,
    canUseSubscription,
    isOrgUser,
    rentalPeriod,
  }) => {
    if (serviceOption !== 'rent') return price; // 購入時
    if (useSubscription && canUseSubscription) return 0; // サブスク
    const table = isOrgUser ? RENTAL_RATE_ORG : RENTAL_RATE_NON_ORG;
    const rate = table[rentalPeriod] ?? table.default ?? 1;
    return Math.round(price * rate);
  };

  const basePrice = useMemo(
    () =>
      calcBasePrice({
        price: Number(product?.price) || 0,
        serviceOption,
        useSubscription,
        canUseSubscription,
        isOrgUser: !!userInfo?.organization,
        rentalPeriod,
      }),
    [
      product?.price,
      serviceOption,
      useSubscription,
      canUseSubscription,
      userInfo?.organization,
      rentalPeriod,
    ],
  );

  const shippingCost = useMemo(() => (inStore ? 0 : SHIPPING_FEE), [inStore]);
  const finalCost = useMemo(() => basePrice + shippingCost, [basePrice, shippingCost]);

  // モーダルUX
  useEffect(() => {
    if (!isPopUpVisible) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && setPopUpVisible(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isPopUpVisible]);

  const openPopUp = () => {
    setServiceOption('rent');
    // サブスク固定運用にするならここで rentalPeriod を固定
    setPopUpVisible(true);
  };
  const closePopUp = () => setPopUpVisible(false);

  // カート投入
  const addToCartHandler = () => {
    if (!product) return;
    // サブスク使わない時は期間必須
    if (!useSubscription && (rentalPeriod === 'default' || !rentalPeriod)) return;
    dispatch(
      addToCart({
        ...product,
        serviceOption,
        inStore,
        useSubscription: canUseSubscription && useSubscription,
        rentalPeriod: !useSubscription ? rentalPeriod : 'unlimited',
        finalPrice: finalCost,
        shippingCost,
      }),
    );
    navigate('/cart');
  };

   const [showFooter, setShowFooter] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > lastScrollY) {
        // ↓スクロール中 → 非表示
        setShowFooter(false);
      } else {
        // ↑スクロール中 → 表示
        setShowFooter(true);
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <div className="productpage-footer">
      <div onClick={openPopUp} 
        className="productpage-footer-container"
        style={{
            bottom: showFooter ? "0" : "-100px", // ← スライドで隠す
            transition: "bottom 0.3s ease-in-out",
          }}>
        <div className="productpage-footer-container-contents">
          <img
            src="https://furupura-assets.s3.ap-northeast-1.amazonaws.com/samples/icon/cartbutton.webp"
         />
          <span className="productpage-footer-container-contents-label">
            {footerLabel}
          </span>
        </div>
      </div>

    {isPopUpVisible && (
      <div className="productpage-footer-overlay" onClick={closePopUp} aria-hidden="true">
        <div
          className="productpage-footer-modal"
          role="dialog"
          aria-modal="true"
          aria-label="レンタル可否"
          onClick={(e) => e.stopPropagation()}
         >
              {/* ───── モーダル内容：加入状態と可否で出し分け ───── */}
              {canUseSubscription ? (
                <>
                {/* 未加入 or 加入中でも不可 */}
                <div className="productpage-footer-modal-userplan">
                    <p className="productpage-footer-modal-userplan-totle">現在ご利用中のプランで商品がレンタル可能です！</p>
                    <div className="productpage-footer-modal-userplan-text">
                      <div className="productpage-footer-modal-userplan-text-1">
                        <p>加入済みプラン</p>
                        <p>利用可能な商品上限金額</p>
                        <p>現在のレンタル着数</p>
                      </div>
                      <div className="productpage-footer-modal-userplan-text-2">
                        <p>{userInfo?.subscription?.name ?? '未加入'}</p>
                        <p>
                          ¥
                          {(
                            userInfo?.subscription?.itemPriceLimit ?? 0
                          ).toLocaleString()}
                        </p>
                        <p>
                          {userInfo?.subscription?.usedItems ?? 0}/
                          {userInfo?.subscription?.maxItems ?? 0}
                        </p>
                      </div>
                    </div>
                    <div className="productpage-footer-modal-userplan-actions">
                      <button
                        className="productpage-footer-modal-userplan-actions-cart"
                        onClick={addToCartHandler}
                      >
                        カートに入れる
                      </button>
                      <button
                        className="productpage-footer-modal-userplan-actions-back"
                        onClick={closePopUp}
                      >
                        商品ページに戻る
                      </button>
                    </div>
                 </div>
                </>
              ) : (
                <>
                <div className="productpage-footer-modal-subscriptionplan">
                  <SubscriptionPlan />
                   <button
                        className="productpage-footer-modal-subscriptionplan-back"
                        onClick={closePopUp}
                    >
                        商品ページに戻る
                  </button>
                </div>
               </>
              )}
          </div>
        </div>
      
      )}
    </div>
  );
};

export default ProductpageFooter;
